import type { CollectibilityData, AssessmentAdminStatus } from '../../types/collectibility';
import type { BuildPageExpansion, ExpansionFeatures } from '../../types/buildExpansion';
import { normalizeDocumentationScore } from '../../utils/documentationScore';
import type { ApiClient } from './client';
import { isGalleryImageType, resolvePublicFileUrl } from '../../utils/imageUrls';
import { getVehicleCompleteness, type VehicleCompletenessResult } from '../../utils/vehicleCompleteness';

type RarityAssessmentGetResponse = {
  success: boolean;
  error?: string;
  assessment?: CollectibilityData | null;
  admin_status?: AssessmentAdminStatus;
};

type RarityAssessmentActionResponse = {
  success: boolean;
  error?: string | null;
  message?: string;
  assessment_status?: string | null;
  previous_status?: string;
  blocking?: boolean;
  removed_artifacts?: string[];
  trace_tail?: string[];
};

export const sharingMethods = {
    // Generate QR code for vehicle
    async generateQRCode(vehicleId: number, options: {
      format?: 'png' | 'svg' | 'pdf';
      size?: 'small' | 'medium' | 'large' | 'xlarge';
      style?: any;
      ref?: string;
    } = {    }): Promise<{
      id: number;
      url: string;
      format: string;
      size: string;
      qr_code_url: string;
      file_path?: string;
      file_size: number;
    }> {
      try {
        // Build query parameters
        const params = new URLSearchParams({
          vehicle_id: vehicleId.toString(),
          ...(options.format && { format: options.format }),
          ...(options.size && { size: options.size }),
          ...(options.style && { style: JSON.stringify(options.style) }),
          ...(options.ref && { ref: options.ref })
        });

        const response = await this.makeRequest<any>(`/generate_qr.php?${params.toString()}`, {
          method: 'GET'
        });

        if (!response.success) {
          throw new Error(response.error || 'Failed to generate QR code');
        }

        return response.data;
      } catch (error: any) {
        console.error('QR code generation failed:', error);
        throw new Error(error.message || 'Failed to generate QR code');
      }
    },

    // Get vehicle sharing settings
    async getVehicleSharingSettings(vehicleId: number): Promise<{
      vehicle: {
        id: number;
        year: number;
        make: string;
        model: string;
        trim: string;
        nickname: string;
        is_public: boolean;
        public_slug: string;
        public_url: string;
        share_count: number;
        view_count: number;
      };
      settings: {
        show_modifications: boolean;
        show_performance: boolean;
        show_images: boolean;
        show_contact_info: boolean;
        show_social_links: boolean;
        custom_message: string;
        theme_color: string;
      };
      stats: {
        total_views: number;
        total_shares: number;
        last_viewed: string;
        last_shared: string;
        share_breakdown: Array<{ share_type: string; count: number; last_occurrence: string }>;
        recent_activity: Array<{ date: string; share_type: string; count: number }>;
      };
      qr_codes: Array<{
        id: number;
        format: string;
        size: string;
        file_path: string;
        download_count: number;
        created_at: string;
      }>;
    }> {
      const response = await this.makeRequest<any>(`/vehicle_settings.php?vehicle_id=${vehicleId}`);

      if (response.success) {
        return response.data || response;
      } else {
        throw new Error(response.error || 'Failed to get sharing settings');
      }
    },

    // Update vehicle sharing settings
    async updateVehicleSharingSettings(vehicleId: number, settings: {
      is_public?: boolean;
      show_modifications?: boolean;
      show_performance?: boolean;
      show_images?: boolean;
      show_contact_info?: boolean;
      show_social_links?: boolean;
      custom_message?: string;
      theme_color?: string;
    }): Promise<{
      vehicle: {
        id: number;
        is_public: boolean;
        public_slug: string;
        public_url: string;
      };
      settings: any;
    }> {
      const response = await this.makeRequest<any>('/vehicle_settings.php', {
        method: 'PUT',
        body: JSON.stringify({
          vehicle_id: vehicleId,
          ...settings
        })
      });

      if (response.success) {
        return response.data || response;
      } else {
        throw new Error(response.error || 'Failed to update sharing settings');
      }
    },

    // Get public vehicle data (for public pages)
    async getPublicVehicle(username: string, slug: string, referrer?: string): Promise<{
      vehicle: {
        id: number;
        year: number;
        make: string;
        model: string;
        trim: string;
        color: string;
        exterior_finish_type?: 'paint' | 'wrap' | 'ppf';
        exterior_finish_color?: string;
        exterior_finish_brand?: string;
        exterior_finish_style?: string;
        chassis?: string;
        engine?: string;
        transmission?: string;
        drivetrain?: string;
        build_status?: 'planned' | 'in_progress' | 'complete' | null;
        nickname: string;
        description: string;
        slug: string;
        public_slug?: string | null;
        canonical_slug?: string | null;
        resolved_via_redirect?: boolean;
        is_public?: boolean;
        is_primary?: boolean;
        created_at: string;
        theme_color: string;
        custom_message: string;
      };
      is_owner: boolean;
      can_edit: boolean;
      is_super_admin_edit?: boolean;
      profileCompletion: VehicleCompletenessResult;
      owner: {
        username: string;
        display_name: string;
        profile_image: string;
      };
      settings: {
        show_modifications: boolean;
        show_performance: boolean;
        show_images: boolean;
        show_contact_info: boolean;
        show_social_links: boolean;
        show_pricing?: boolean;
      };
      modifications: any[];
      images: any[];
      performance: any;
      stats: any;
      collectibility?: CollectibilityData;
      awards?: Array<{
        id: number;
        title: string;
        placement: string | null;
        event_title: string | null;
        event_slug: string | null;
        published_at: string | null;
      }>;
      view_count?: number;
      favorite_count?: number;
      is_favorited?: boolean;
      like_count?: number;
      user_has_liked?: boolean;
      expansion: BuildPageExpansion;
      expansion_features?: ExpansionFeatures;
      meta: {
        title: string;
        description: string;
        url: string;
        image: string;
      };
    }> {
      const params = new URLSearchParams({ username, slug });
      if (referrer) {
        params.append('ref', referrer);
      }

      const response = await this.makeRequest<any>(`/vehicle_by_slug.php?${params.toString()}`, {
        method: 'GET'
      });

      if (response.success && response.data) {
        const vehicleData = response.data;
        const showPricing = vehicleData.settings?.show_pricing !== false;

        const rawImages = vehicleData.images_list || [];
        const rawModifications = vehicleData.modifications_list || [];
        const profileCompletion = getVehicleCompleteness({
          engine: vehicleData.engine,
          transmission: vehicleData.transmission,
          drivetrain: vehicleData.drivetrain,
          chassis: vehicleData.chassis,
          description: vehicleData.description,
          imageCount: rawImages.length,
          modificationCount: rawModifications.length,
        });

        // Transform the response to match the expected format
        return {
          vehicle: {
            id: vehicleData.id,
            year: vehicleData.year,
            make: vehicleData.make,
            model: vehicleData.model,
            trim: vehicleData.trim,
            color: vehicleData.color || '',
            exterior_finish_type: vehicleData.exterior_finish_type || 'paint',
            exterior_finish_color: vehicleData.exterior_finish_color || '',
            exterior_finish_brand: vehicleData.exterior_finish_brand || '',
            exterior_finish_style: vehicleData.exterior_finish_style || '',
            chassis: vehicleData.chassis || '',
            engine: vehicleData.engine || '',
            transmission: vehicleData.transmission || '',
            drivetrain: vehicleData.drivetrain || '',
            build_status: vehicleData.build_status || null,
            nickname: vehicleData.nickname || '',
            description: vehicleData.description || '',
            slug: vehicleData.public_slug || vehicleData.display_name || `${vehicleData.year}-${vehicleData.make}-${vehicleData.model}`.toLowerCase().replace(/\s+/g, '-'),
            public_slug: vehicleData.public_slug || null,
            canonical_slug: vehicleData.canonical_slug || vehicleData.public_slug || null,
            resolved_via_redirect: Boolean(vehicleData.resolved_via_redirect),
            is_public: vehicleData.is_public !== false && vehicleData.is_public !== 0,
            is_primary: Boolean(vehicleData.is_primary),
            created_at: vehicleData.created_at,
            theme_color: vehicleData.settings?.theme_color || '#007AFF',
            custom_message: vehicleData.settings?.custom_message || ''
          },
          is_owner: vehicleData.user_context?.is_owner === true,
          can_edit: vehicleData.user_context?.can_edit === true,
          is_super_admin_edit: vehicleData.user_context?.is_super_admin_edit === true,
          profileCompletion,
          owner: {
            username: vehicleData.owner?.username || username,
            display_name: vehicleData.owner?.display_name || username,
            profile_image: resolvePublicFileUrl(vehicleData.owner?.profile_image) || vehicleData.owner?.profile_image || '',
          },
          settings: {
            show_modifications: vehicleData.settings?.show_modifications !== false,
            show_performance: vehicleData.settings?.show_performance !== false,
            show_images: vehicleData.settings?.show_images !== false,
            show_contact_info: vehicleData.settings?.show_contact_info === true,
            show_social_links: vehicleData.settings?.show_social_links === true,
            show_pricing: showPricing,
          },
          modifications: (vehicleData.modifications_list || []).map((mod: any) => ({
            ...mod,
            cost: showPricing ? mod.cost : undefined,
            image_url: resolvePublicFileUrl(mod.image_url) ?? mod.image_url,
          })),
          images: (vehicleData.images_list || [])
            .filter((img: { image_type?: string }) => isGalleryImageType(img.image_type))
            .map((img: { image_url?: string; [key: string]: unknown }) => ({
              ...img,
              image_url: resolvePublicFileUrl(img.image_url) ?? img.image_url,
            })),
          performance: vehicleData.settings?.show_performance !== false ? (vehicleData.performance || null) : null,
          stats: {
            ...(vehicleData.stats || {}),
            total_views: vehicleData.stats?.total_views ?? vehicleData.view_count ?? 0,
            total_shares: vehicleData.stats?.total_shares ?? vehicleData.share_count ?? 0,
            last_viewed: vehicleData.stats?.last_viewed ?? vehicleData.last_viewed_at ?? null,
            last_shared: vehicleData.stats?.last_shared ?? vehicleData.last_shared_at ?? null,
            view_count: vehicleData.view_count ?? vehicleData.stats?.total_views ?? 0,
            favorite_count: vehicleData.favorite_count ?? vehicleData.stats?.favorite_count ?? 0,
            like_count: vehicleData.like_count ?? vehicleData.stats?.like_count ?? 0,
            share_breakdown: vehicleData.stats?.share_breakdown || [],
          },
          view_count: vehicleData.view_count ?? vehicleData.stats?.total_views ?? 0,
          favorite_count: vehicleData.favorite_count ?? 0,
          is_favorited: Boolean(vehicleData.is_favorited),
          like_count: Number(vehicleData.like_count ?? 0),
          user_has_liked: Boolean(vehicleData.user_has_liked),
          collectibility: {
            base_rarity_stars: vehicleData.base_rarity_stars ?? null,
            base_collector_points: vehicleData.base_collector_points ?? null,
            base_rarity_level: vehicleData.base_rarity_level ?? null,
            production_numbers: vehicleData.production_numbers ?? null,
            vehicle_overview: vehicleData.vehicle_overview ?? null,
            assessment_version: vehicleData.assessment_version ?? null,
            assessment_confidence:
              vehicleData.assessment_confidence === 'high' ||
              vehicleData.assessment_confidence === 'medium' ||
              vehicleData.assessment_confidence === 'low'
                ? vehicleData.assessment_confidence
                : null,
            assessment_status: vehicleData.assessment_status ?? null,
          },
          awards: (vehicleData.awards || []).map((a: any) => ({
            id: a.id,
            title: a.title,
            placement: a.placement,
            event_title: a.event_title,
            event_slug: a.event_slug,
            published_at: a.published_at,
          })),
          // Build-page expansion payload (all failure-isolated server side).
          expansion: {
            build_profile: vehicleData.build_profile ?? null,
            build_summary: vehicleData.build_summary ?? null,
            stats_strip: Array.isArray(vehicleData.stats_strip) ? vehicleData.stats_strip : [],
            power_to_weight: vehicleData.power_to_weight ?? null,
            factory_comparison: vehicleData.factory_comparison ?? null,
            achievements: Array.isArray(vehicleData.achievements) ? vehicleData.achievements : [],
            documentation_score: normalizeDocumentationScore(vehicleData.documentation_score),
            performance_records: Array.isArray(vehicleData.performance_records)
              ? vehicleData.performance_records
              : [],
            dyno_summary: vehicleData.dyno_summary ?? null,
            media_albums: Array.isArray(vehicleData.media_albums) ? vehicleData.media_albums : [],
            media_links: Array.isArray(vehicleData.media_links) ? vehicleData.media_links : [],
            contributors: Array.isArray(vehicleData.contributors) ? vehicleData.contributors : [],
          },
          expansion_features: {
            share_cards_enabled: vehicleData.expansion_features?.share_cards_enabled !== false,
          },
          meta: {
            title: `${vehicleData.year} ${vehicleData.make} ${vehicleData.model}${vehicleData.nickname ? ` "${vehicleData.nickname}"` : ''}`,
            description: vehicleData.description || `Check out this ${vehicleData.year} ${vehicleData.make} ${vehicleData.model} and its modifications on biltspec.com`,
            url: `${window.location.origin}/${username}/${slug}`,
            image: `${window.location.origin}/api/og_image.php?username=${encodeURIComponent(username)}&slug=${encodeURIComponent(slug)}`,
          }
        };
      } else {
        throw new Error(response.error || 'Failed to get public vehicle data');
      }
    },


    async getRarityAssessment(vehicleId: number): Promise<{
      assessment: CollectibilityData | null;
      admin_status?: AssessmentAdminStatus;
    }> {
      const response = (await this.makeRequest<unknown>(
        `/assess_vehicle_rarity.php?vehicle_id=${vehicleId}`,
        { method: 'GET' }
      )) as RarityAssessmentGetResponse;
      if (response.success) {
        return {
          assessment: response.assessment ?? null,
          admin_status: response.admin_status,
        };
      }
      throw new Error(response.error || 'Failed to load rarity assessment');
    },

    async getRarityAssessmentStatus(vehicleId: number): Promise<{
      assessment: CollectibilityData | null;
      admin_status?: AssessmentAdminStatus;
    }> {
      const response = (await this.makeRequest<unknown>(
        `/assess_vehicle_rarity.php?vehicle_id=${vehicleId}`,
        { method: 'GET' }
      )) as RarityAssessmentGetResponse;
      if (response.success) {
        return {
          assessment: response.assessment ?? null,
          admin_status: response.admin_status,
        };
      }
      throw new Error(response.error || 'Failed to load rarity assessment');
    },


    async scheduleRarityAssessment(vehicleId: number, action: 'schedule' | 'reset' | 'reset_and_run' = 'schedule', options?: {
      clear_scores?: boolean;
      clear_logs?: boolean;
    }): Promise<{
      assessment_status: string | null;
      blocking?: boolean;
      message?: string;
      error?: string | null;
      removed_artifacts?: string[];
      trace_tail?: string[];
    }> {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 180000);

      try {
        const response = (await this.makeRequest<unknown>('/assess_vehicle_rarity.php', {
          method: 'POST',
          body: JSON.stringify({
            vehicle_id: vehicleId,
            action,
            clear_scores: options?.clear_scores ?? false,
            clear_logs: options?.clear_logs ?? true,
          }),
          signal: controller.signal,
        })) as RarityAssessmentActionResponse;
        if (response.success) {
          return {
            assessment_status: response.assessment_status ?? (action === 'reset' ? null : 'pending'),
            blocking: response.blocking,
            message: response.message,
            error: response.error,
            removed_artifacts: response.removed_artifacts,
            trace_tail: response.trace_tail,
          };
        }
        throw new Error(response.error || response.message || 'Failed to schedule rarity assessment');
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          throw new Error('Assessment timed out after 3 minutes. Check server trace log');
        }
        throw err;
      } finally {
        window.clearTimeout(timeout);
      }
    },

    async resetRarityAssessmentPipeline(vehicleId: number, options?: {
      clear_scores?: boolean;
      clear_logs?: boolean;
    }): Promise<{
      assessment_status: string | null;
      message?: string;
      removed_artifacts?: string[];
    }> {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 60000);
      try {
        const response = (await this.makeRequest<unknown>('/assess_vehicle_rarity.php', {
          method: 'POST',
          body: JSON.stringify({
            vehicle_id: vehicleId,
            action: 'reset',
            clear_scores: options?.clear_scores ?? false,
            clear_logs: options?.clear_logs ?? true,
          }),
          signal: controller.signal,
        })) as RarityAssessmentActionResponse;
        if (response.success) {
          return {
            assessment_status: response.assessment_status ?? null,
            message: response.message,
            removed_artifacts: response.removed_artifacts,
          };
        }
        throw new Error(response.error || 'Failed to reset assessment pipeline');
      } finally {
        window.clearTimeout(timeout);
      }
    },

    async resetAndRunRarityAssessment(vehicleId: number, options?: {
      clear_scores?: boolean;
    }): Promise<{
      assessment_status: string | null;
      blocking?: boolean;
      message?: string;
      error?: string | null;
      trace_tail?: string[];
    }> {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 180000);
      try {
        const response = (await this.makeRequest<unknown>('/assess_vehicle_rarity.php', {
          method: 'POST',
          body: JSON.stringify({
            vehicle_id: vehicleId,
            action: 'reset_and_run',
            clear_scores: options?.clear_scores ?? false,
            clear_logs: true,
          }),
          signal: controller.signal,
        })) as RarityAssessmentActionResponse;
        if (response.success) {
          return {
            assessment_status: response.assessment_status ?? 'pending',
            blocking: response.blocking,
            message: response.message,
            error: response.error,
            trace_tail: response.trace_tail,
          };
        }
        throw new Error(response.error || response.message || 'Failed to reset and run assessment');
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          throw new Error('Assessment timed out after 3 minutes. Check server trace log');
        }
        throw err;
      } finally {
        window.clearTimeout(timeout);
      }
    },

    async cancelRarityAssessment(vehicleId: number): Promise<{
      assessment_status: string;
      previous_status?: string;
      message?: string;
    }> {
      const response = (await this.makeRequest<unknown>(
        `/assess_vehicle_rarity.php?vehicle_id=${vehicleId}`,
        { method: 'DELETE' }
      )) as RarityAssessmentActionResponse;
      if (response.success) {
        return {
          assessment_status: response.assessment_status ?? 'error',
          previous_status: response.previous_status,
          message: response.message,
        };
      }
      throw new Error(response.error || 'Failed to cancel rarity assessment');
    }
} satisfies ThisType<ApiClient>;
